import SpaceBetween from '@risk-smart/themed-cloudscape-components/space-between';
import Button from '@risksmart-app/components/src/button';
import { PageNotFound } from '@risksmart-app/components/src/errors/errors';
import { useGetGuidParam } from '@risksmart-app/components/src/routes/routes.utils';
import { ParentTypes as Parent_Type_Enum } from '@risksmart-app/domain/src/types/consts/parent-type';
import type { IssueTaxonomyKeys } from '@risksmart-app/shared/forms/issues/types';
import type { FC } from 'react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import type { ObjectToolbarConfig } from 'src/blocks/object-toolbar';
import { ObjectToolbar } from 'src/blocks/object-toolbar';
import { useObjectToolbarFormStore } from 'src/blocks/object-toolbar/useObjectToolbarFormStore';
import ControlledTabs from 'src/components/controlled-tabs';
import DeleteModal from 'src/components/delete-modal/DeleteModal';
import TabSettingsModal from 'src/components/tab-settings-modal';
import { useDeleteInternalAudits } from 'src/hooks/mutations/internal-audit';
import {
  useGetInternalAuditById,
  useGetInternalAuditEntitiesRegister,
} from 'src/hooks/queries';
import { PageLayout } from 'src/layouts';
import { Permission } from 'src/rbac/Permission';
import { useGetDetailParentPath } from 'src/routes/useGetDetailParentPath';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { useDeleteResultNotification } from '@/hooks/useMutationResultNotification';
import useTabs from '@/hooks/useTabs';
import { getFriendlyId } from '@/utils/friendlyId';
import { internalAuditDetailsUrl } from '@/utils/urls';

type Props = {
  activeTabId:
    | 'actions'
    | 'details'
    | 'linkedItems'
    | 'reports'
    | 'risks'
    | IssueTaxonomyKeys;
};

const Page: FC<Props> = ({ activeTabId }) => {
  const internalAuditId = useGetGuidParam('internalAuditId');
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [isDeleted, setIsDeleted] = useState(false);
  const [showTabSettings, setShowTabSettings] = useState(false);
  const objectToolbarEnabled = useIsFeatureFlagEnabled('dynamic_forms');
  // Form-editor opener bridged up from the active tab's FormMenu (relocate mode).
  // Present only on the details tab when dynamic_forms are on.
  const formConfig = useObjectToolbarFormStore((s) => s.formConfigState);
  const navigate = useNavigate();
  const parentUrl = useGetDetailParentPath(internalAuditId);
  const { deleteInternalAudits, loading: deleting } = useDeleteInternalAudits();

  const { t } = useTranslation(['common']);
  const { t: st } = useTranslation(['common'], { keyPrefix: 'internalAudits' });
  const { refetch: refetchInternalAuditsRegister } =
    useGetInternalAuditEntitiesRegister({ queryArgs: {} });

  const {
    data,
    error,
    loading,
    refetch: refetchInternalAudit,
  } = useGetInternalAuditById({ queryArgs: { internalAuditId } });

  if (error) {
    throw error;
  }

  const internalAudit = data?.internal_audit_entity[0];
  const tabs = useTabs({
    parentType: Parent_Type_Enum.InternalAuditEntity,
    parent: internalAudit,
    hrefRoot: internalAuditDetailsUrl(internalAuditId),
  });
  const onDelete = useDeleteResultNotification({
    entityName: st('entity'),
    asyncAction: async () => {
      if (!internalAudit) {
        return false;
      }
      await deleteInternalAudits([internalAudit.Id]);
      setIsDeleted(true);
      refetchInternalAudit();
      refetchInternalAuditsRegister();
      await navigate(parentUrl);

      return true;
    },
  });

  const counter =
    internalAudit &&
    `(${getFriendlyId(
      Parent_Type_Enum.InternalAuditEntity,
      internalAudit.SequentialId
    )})`;
  const fallbackTitle = st('fallback_title');

  // Object-level toolbar (CUS-165) — flag-gated. Relocates the header Delete
  // into the toolbar's danger (⋮) group and hosts the tab-settings (and Edit
  // form, when bridged) openers in the overflow "Edit" group. The Save disc /
  // Cancel X come from the active details form via the toolbar bridge store.
  // Declared before the early returns below to keep hook order stable.
  const toolbarConfig: ObjectToolbarConfig = useMemo(
    () => ({
      header: {
        title: internalAudit?.Title ?? fallbackTitle,
        counter: counter || undefined,
      },
      parentObject: internalAudit,
      save: true,
      actions: {
        overflow: [
          {
            id: 'edit',
            label: t('edit'),
            actions: [
              ...(formConfig
                ? [
                    {
                      id: 'edit-form',
                      label: t('edit_form'),
                      iconName: 'file-06' as const,
                      onClick: formConfig.openConfigure,
                    },
                  ]
                : []),
              {
                id: 'edit-tabs',
                label: t('edit_tabs'),
                iconName: 'layers-three-01',
                onClick: () => setShowTabSettings(true),
              },
            ],
          },
        ],
        danger: [
          {
            id: 'delete',
            label: st('delete_button'),
            iconName: 'trash-01',
            permission: 'delete:internal_audit_entity',
            // Hydration gate (ENG-616) — same as risks/assets.
            disabled: !internalAudit,
            onClick: () => setIsDeleteModalVisible(true),
          },
        ],
      },
    }),
    [internalAudit, fallbackTitle, counter, t, st, formConfig]
  );

  if (isDeleted) {
    return <></>;
  }

  if (!loading && !internalAudit?.Id) {
    throw new PageNotFound(
      `Internal audit with id ${internalAuditId} not found`
    );
  }

  return (
    <PageLayout
      headerOverride={
        objectToolbarEnabled ? (
          <ObjectToolbar config={toolbarConfig} />
        ) : undefined
      }
      actions={
        <Permission
          permission={'delete:internal_audit_entity'}
          parentObject={internalAudit}
        >
          <SpaceBetween direction={'horizontal'} size={'xs'}>
            <Button
              variant={'normal'}
              formAction={'none'}
              // Wait for the entity to hydrate. `onDelete` guards on
              // `if (!internalAudit) return false`, which surfaces as a bare
              // "Failed to save changes" with NO request issued, so a click
              // inside the hydration window looks like a broken delete
              // (ENG-616). Disabling also restores Playwright's actionability
              // wait.
              disabled={!internalAudit}
              onClick={() => {
                setIsDeleteModalVisible(true);
              }}
            >
              {st('delete_button')}
            </Button>
          </SpaceBetween>
        </Permission>
      }
      meta={{
        title: fallbackTitle,
      }}
      title={internalAudit?.Title}
      counter={counter}
    >
      <ControlledTabs
        tabs={tabs}
        activeTabId={activeTabId}
        variant={'container'}
        parentType={Parent_Type_Enum.InternalAuditEntity}
        parent={internalAudit}
        disableSettings={objectToolbarEnabled}
      />

      <TabSettingsModal
        parentType={Parent_Type_Enum.InternalAuditEntity}
        isVisible={showTabSettings}
        onDismiss={() => setShowTabSettings(false)}
        parent={internalAudit}
      />

      <DeleteModal
        loading={deleting}
        isVisible={isDeleteModalVisible}
        header={st('delete_modal_title')}
        onDelete={onDelete}
        onDismiss={() => setIsDeleteModalVisible(false)}
      >
        {st('confirm_delete_message')}
      </DeleteModal>
    </PageLayout>
  );
};

export default Page;
